import React, { useRef, useState, useLayoutEffect } from 'react';
import ReactHtmlParser from 'react-html-parser';
import { Box, BoxProps } from '@components/Box';
import { CloudImage } from '@components/CloudImage';
import {
  LeftColumn,
  RightColumn,
  RightColumnAvatar,
  DataColumns,
  ManageTextPairing,
  PriorityTextPairing,
  FilterTextPairing,
  BlurbsAsset1Wrap,
  BlurbsAsset3Wrap,
  ListCloudImage,
  ListWrapper,
  ListAvatarsDiv,
  ListAvatar,
  ListAvatarText,
} from './FeatureBlurbs.styles';
import { useScrollController } from '@context/ScrollControllerProvider/useScrollController';
import ScrollMagic from 'scrollmagic';
import { gsap, Linear } from 'gsap';
import { useIsMounted } from '@hooks/useIsMounted';
import { useThemeMediaQuery } from '@hooks/useThemeMediaQuery';
import { useOnScreen } from '@hooks/useOnScreen';
import { useTranslation } from 'react-i18next';

interface FeatureBlurbsProps
  extends Readonly<{
    backgroundColor: BoxProps['backgroundColor'];
  }> {}

export const FeatureBlurbs: React.FC<FeatureBlurbsProps> = ({ backgroundColor }) => {
  const { t } = useTranslation('guestList');
  const featureData = t('feature');
  const sectionId = 'Smart_Guest_Tags';

  const listAvatarsDivRef = useRef<HTMLDivElement>(null);
  const listAvatarsParentRef = useRef<HTMLDivElement>(null);

  const scrollController = useScrollController();
  const isMounted = useIsMounted();
  const isMobile = useThemeMediaQuery('below_sm');
  const animationRef = useRef<HTMLDivElement>(null);
  const onScreen = useOnScreen(animationRef, '0px');
  const [isVisible, setIsVisible] = useState(false);

  useLayoutEffect(() => {
    const listAvatarsDivEl = listAvatarsDivRef.current;
    const listAvatarsParentEl = listAvatarsParentRef.current;
    let timeout: ReturnType<typeof setTimeout>;
    let scene: InstanceType<typeof ScrollMagic.Scene> | undefined;
    let sceneTimeline: ReturnType<typeof gsap.timeline>;
    const refreshTimeline = () => {
      if (scrollController && listAvatarsParentEl && listAvatarsDivEl) {
        clearTimeout(timeout);
        timeout = setTimeout(() => {
          const scrollPos = scrollController?.scrollPos() as number;
          sceneTimeline?.progress(0).kill();
          initTimeline();
          if (scene) {
            scene.removePin(true);
            scene.setPin(listAvatarsDivEl, { pushFollowers: false });
            scene.update();
          }
          scrollController.scrollTo(scrollPos);
        }, 300);
      }
    };
    const initTimeline = () => {
      if (scrollController && isMounted && listAvatarsDivEl) {
        sceneTimeline = gsap.timeline({ paused: false, defaults: { ease: Linear.easeNone } });
        if (!isVisible && onScreen) {
          sceneTimeline.to(listAvatarsDivEl.querySelectorAll('.list-avatar'), { marginLeft: isMobile ? '-5rem' : '-5.7rem' }, 0.5);
          sceneTimeline.to(listAvatarsDivEl.querySelectorAll('.list-avatar-container'), { paddingLeft: isMobile ? '4rem' : '6rem' }, 0.5);
          setIsVisible(true);
        }
      }
    };
    initTimeline();
    window.addEventListener('resize', refreshTimeline);
    return () => {
      window.removeEventListener('resize', refreshTimeline);
    };
  }, [scrollController, isMounted, isMobile, onScreen, isVisible]);

  return (
    <Box id={sectionId} ref={listAvatarsParentRef}>
      <Box backgroundColor={backgroundColor} paddingY={[6, 10, 12]} style={{ maxWidth: '1300px', margin: 'auto' }} ref={listAvatarsDivRef}>
        <DataColumns verticalAlign="start" marginBottom={0}>
          <LeftColumn width="four" offset="one">
            <BlurbsAsset1Wrap marginTop={[7, 5]}>
              <CloudImage alt={featureData.invites.altText} width="100%" height="100%" src={featureData.invites.image} />
            </BlurbsAsset1Wrap>
          </LeftColumn>
          <RightColumn width="four" offset="one">
            <ManageTextPairing
              alignment="left"
              title={featureData.invites.title}
              titleColor="neutra6"
              titleVariant="calloutHed32"
              titleTagName="h3"
              body={featureData.invites.content}
              bodyVariant="dek24"
              bodyColor="neutra5"
              marginTop={2}
            />
          </RightColumn>
        </DataColumns>
        <DataColumns reverse={true} verticalAlign="center" marginBottom={[5, 12, 12]}>
          <LeftColumn width="four" offset="one">
            <PriorityTextPairing
              alignment="left"
              title={ReactHtmlParser(featureData.prioritize.title)}
              titleColor="neutra6"
              titleVariant="calloutHed32"
              titleTagName="h3"
              body={featureData.prioritize.content}
              bodyVariant="dek24"
              bodyColor="neutra5"
            />
          </LeftColumn>
          <RightColumnAvatar width="six" offset="one">
            <ListWrapper ref={animationRef}>
              <ListCloudImage alt={featureData.prioritize.altText} src={featureData.aTag} />
              <ListAvatarsDiv className="list-avatar-container">
                <ListAvatar src="common/avatars/avatars_photo_avatar-18.jpg" />
                <ListAvatar src="common/avatars/avatars_photo_avatar-9.jpg" />
                <ListAvatar src="common/avatars/avatars_photo_avatar-37.jpg" />
                <ListAvatar src="common/avatars/avatars_photo_avatar-16.jpg" />
                <ListAvatar src="common/avatars/avatars_photo_avatar-34.jpg" />
                <ListAvatarText src="guest-list/tags-overview/98-v2.svg" />
              </ListAvatarsDiv>
            </ListWrapper>
            <ListWrapper>
              <ListCloudImage alt={featureData.prioritize.altText} src={featureData.bTag} />
              <ListAvatarsDiv className="list-avatar-container">
                <ListAvatar src="common/avatars/avatars_photo_avatar-24.jpg" />
                <ListAvatar src="common/avatars/avatars_photo_avatar-4.jpg" />
                <ListAvatar src="common/avatars/avatars_photo_avatar-17.jpg" />
                <ListAvatarText src="guest-list/tags-overview/54-v2.svg" />
              </ListAvatarsDiv>
            </ListWrapper>
            <ListWrapper>
              <ListCloudImage alt={featureData.prioritize.altText} src={featureData.cTag} />
              <ListAvatarsDiv className="list-avatar-container">
                <ListAvatar src="common/avatars/avatars_photo_avatar-11.jpg" />
                <ListAvatar src="common/avatars/avatars_photo_avatar-14.jpg" />
                <ListAvatarText src="guest-list/tags-overview/37-v2.svg" />
              </ListAvatarsDiv>
            </ListWrapper>
          </RightColumnAvatar>
        </DataColumns>
        <DataColumns verticalAlign="center">
          <LeftColumn width="four" offset="one">
            <BlurbsAsset3Wrap marginTop={[0, -3]}>
              <CloudImage alt={featureData.sort.altText} width="100%" height="100%" src={featureData.sort.image} />
            </BlurbsAsset3Wrap>
          </LeftColumn>
          <RightColumn width="four" offset="one">
            <FilterTextPairing
              alignment="left"
              title={featureData.sort.title}
              titleColor="neutra6"
              titleVariant="calloutHed32"
              titleTagName="h3"
              body={featureData.sort.content}
              bodyVariant="dek24"
              bodyColor="neutra5"
            />
          </RightColumn>
        </DataColumns>
      </Box>
    </Box>
  );
};
